import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { create } from 'zustand';
import { API_URL, type ApiItem, unwrapData } from '@/lib/api';
import { createApiClient } from '@/lib/api-client';

/** The sold packages as `GET /v1/public/plans` returns them: the admin app is where names, copy and numbers are edited. */
export type Catalog = ApiItem<'/v1/public/plans'>;
export type CatalogPlan = Catalog['plans'][number];
type Lang = 'th' | 'en';

export const copyLang = (language: string | undefined): Lang => (language?.startsWith('en') ? 'en' : 'th');

const publicApi = createApiClient({ baseUrl: API_URL, credentials: 'omit' });

interface CatalogState {
  catalog: Catalog | null;
  load: () => Promise<void>;
}

/** Plain store (not react-query): `errorMessageOf` and other non-hook code read it too. Loaded once at start. */
export const usePlanCatalog = create<CatalogState>((set, get) => ({
  catalog: null,
  load: async () => {
    if (get().catalog) return;
    try {
      set({ catalog: await unwrapData(publicApi.GET('/v1/public/plans')) });
    } catch {
      // the messages that name a plan fall back to a plain sentence; nothing else depends on it
    }
  },
}));

/** Display name of a plan code's package ("line-pro-year" → "Pro") from the catalog, if it is sold. */
export function planName(catalog: Catalog | null, code: string, lang: Lang): string | undefined {
  const base = code.replace(/-year$/, '');
  return catalog?.plans.find((p) => p.code === base)?.marketing[lang].displayName;
}

/** The first (cheapest by sort) plan that includes `module`: "from which package this feature exists". */
export function firstPlanWith(catalog: Catalog | null, module: string): CatalogPlan | undefined {
  return catalog?.plans.find((p) => p.modules.includes(module));
}

/** The first plan whose limit for `key` is higher than `current` (or unlimited), i.e. the one to look at after hitting it. */
export function nextPlanFor(catalog: Catalog | null, key: 'members' | 'branches', current: number) {
  return catalog?.plans.find((p) => {
    const limit = p.limits[key];
    return limit === undefined || limit > current;
  });
}

/** "แพ็กเกจ Premium" / "the Premium plan" for the package a feature starts at; a plain phrase while unknown (`t` any namespace). */
export function planRef(catalog: Catalog | null, module: string, lang: Lang, t: TFunction): string {
  const name = firstPlanWith(catalog, module)?.marketing[lang].displayName;
  return name ? t('planRef', { ns: 'common', name }) : t('planRefUnknown', { ns: 'common' });
}

/** Hook: re-renders when the catalog arrives and when the language changes. */
export function usePlanRef(module: string): string {
  const { t, i18n } = useTranslation('common');
  const catalog = usePlanCatalog((s) => s.catalog);
  return planRef(catalog, module, copyLang(i18n.language), t);
}

const bahtOf = (minor: number): string => `฿${(minor / 100).toLocaleString('th-TH')}`;

/**
 * Line for "the package's account / branch limit is used": names the next package that allows more (name and its
 * accounts / branches text from the catalog), or the extra-branch price when no bigger package exists. Empty when the
 * catalog is not loaded: the caller shows the plain sentence.
 */
export function planLimitMessage(
  catalog: Catalog | null,
  key: string,
  limit: unknown,
  lang: Lang,
  t: TFunction<'errors'>,
): string {
  if ((key !== 'members' && key !== 'branches') || typeof limit !== 'number') return '';
  const next = nextPlanFor(catalog, key, limit);
  // bought extras (฿ per account / branch a month) are the other way out; the price and the first package that sells them come from the catalogue
  const sold = catalog?.extras?.[key === 'members' ? 'seats' : 'branches'];
  const buy = sold
    ? t(`planLimit.${key}Buy`, {
        fee: bahtOf(sold.monthlyMinor),
        plan: planName(catalog, sold.planCodes[0] ?? '', lang) ?? '',
      })
    : '';
  if (next) {
    const copy = next.marketing[lang];
    return `${t(`planLimit.${key}Next`, {
      count: limit,
      plan: copy.displayName,
      facts: key === 'members' ? copy.accounts : copy.branches,
    })}${buy ? ` ${buy}` : ''}`;
  }
  // no bigger package: bought extras are the way out (members: the price line; branches: the extra-branch sentence)
  if (key === 'members') return buy;
  return sold ? t('planLimit.branchesExtra', { count: limit, fee: bahtOf(sold.monthlyMinor) }) : '';
}

/** What the "add branch" button says once the package's branch limit is used (`kind`: which sentence, with its values). */
export function branchLimitNote(catalog: Catalog | null, planCode: string, limit: number, lang: Lang) {
  const next = nextPlanFor(catalog, 'branches', limit);
  if (next) return { kind: 'next' as const, plan: next.marketing[lang].displayName };
  const sold = catalog?.extras?.branches;
  return sold?.planCodes.includes(planCode.replace(/-year$/, ''))
    ? { kind: 'extra' as const, fee: bahtOf(sold.monthlyMinor) }
    : { kind: 'full' as const };
}
